import { createRoute, Link, redirect } from '@tanstack/react-router';
import { IconPlus, IconEdit, IconBuildingBank, IconTrash } from '@tabler/icons-react';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { useAdminProviders, useDeleteProvider } from '../../../../hooks/useAdminProviders.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';
import { formatDate } from '../../../../lib/format.js';
import { useState } from 'react';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/providers',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'super_admin') throw redirect({ to: '/app' });
  },
  component: ProviderListPage,
});

const CURATION_LABELS: Record<string, string> = {
  shell: 'Shell',
  curated: 'Curated',
  needs_review: 'Needs review',
  archived: 'Archived',
};

const CURATION_TONE: Record<string, string> = {
  shell: 'bg-cream-deep text-muted',
  curated: 'bg-success-bg text-success',
  needs_review: 'bg-warn-bg text-warn',
  archived: 'bg-danger-bg text-danger',
};

function ProviderListPage() {
  const [q, setQ] = useState('');
  const { data, isLoading, error } = useAdminProviders(q || undefined);
  const del = useDeleteProvider();

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Archive provider "${name}"? You can restore via the database later.`)) return;
    try {
      await del.mutateAsync(id);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <div>
      <AdminPageHeader
        title="Providers"
        description="The master list of Australian education providers. Tenants only see providers you share with them."
        actions={
          <Link to="/app/admin/providers/new">
            <Button leftIcon={<IconPlus size={16} />}>New provider</Button>
          </Link>
        }
      />

      <div className="mb-6 max-w-md">
        <Input
          placeholder="Search by name, short name, or CRICOS code…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {isLoading && <p className="text-muted">Loading…</p>}
      {error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load providers: {(error as Error).message}
        </p>
      )}

      {data && data.items.length === 0 && (
        <div className="border border-dashed border-line-strong rounded-xl p-12 text-center bg-white/40">
          <IconBuildingBank size={32} className="text-muted-soft mx-auto mb-3" />
          <p className="text-ink-soft font-medium">No providers yet</p>
          <p className="text-sm text-muted mt-1">
            Add your first provider, or run the CRICOS import script.
          </p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="bg-white border border-line rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream/60 border-b border-line">
              <tr className="text-left text-muted text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-medium">Provider</th>
                <th className="px-5 py-3 font-medium">CRICOS code</th>
                <th className="px-5 py-3 font-medium">State</th>
                <th className="px-5 py-3 font-medium">State of curation</th>
                <th className="px-5 py-3 font-medium">Last verified</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.items.map((p) => (
                <tr key={p.id} className="hover:bg-cream/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-ink">{p.name}</div>
                    {p.shortName && <div className="text-xs text-muted">{p.shortName}</div>}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-ink-soft">
                    {p.cricosProviderCode ?? '—'}
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft">{p.primaryState ?? '—'}</td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CURATION_TONE[p.curationState] ?? ''}`}
                    >
                      {CURATION_LABELS[p.curationState] ?? p.curationState}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-muted text-xs">
                    {formatDate(p.lastVerifiedAt)}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to="/app/admin/providers/$providerId"
                        params={{ providerId: p.id }}
                        className="p-1.5 rounded hover:bg-cream-deep text-muted hover:text-navy"
                        aria-label="Edit"
                      >
                        <IconEdit size={16} />
                      </Link>
                      <button
                        onClick={() => handleDelete(p.id, p.name)}
                        className="p-1.5 rounded hover:bg-danger-bg text-muted hover:text-danger"
                        aria-label="Archive"
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
